/**
 * Окна поверх боя (C4). Подключение — одна строка в main.tsx: `registerWindows()`. Колбэки кнопок — `setWindowActions`.
 *
 * Окно — папка `<окно>/`: `view.tsx` (пропсы), `window.tsx` (стор, колбэки, `register()`), `actions.ts`, `messages.ts`,
 * `styles.css`, `model.ts`. Реестр собирается сам: новое окно добавляет файлы и не правит общие.
 */
import type { FriendsResponse, LeagueResponse, PlayerState } from "@newfungame/contracts";
import type { BoostOption } from "@newfungame/engine";
import { appStore, openWindow, setBoostOffer, setPlayer, type WindowRoute } from "../../app";
import { addMessages } from "../../i18n";
import { NOOP_ACTIONS, setWindowActions, type WindowActions } from "./actions";
import { windowMessages } from "./messages";

export { StreakCard } from "./streak/window";
export { setWindowActions, windowActions, type WindowActions } from "./actions";
export { BOOST_HINT_KEY } from "./boost-choice/window";
export { recommendBoost } from "./boost-choice/model";
export { upgradeAdvice, type UpgradeAdvice } from "./defeat/model";
export { streakDays, type StreakDayView } from "./streak/model";
export { welcomeBackModel, type WelcomeBackModel } from "./welcome-back/model";
export { windowMessages } from "./messages";

declare global {
  interface Window {
    /**
     * Отладка окон с мок-API (`?mock=1`): открыть окно, подложить предложение усилений и игрока; `calls` — журнал
     * вызванных колбэков (e2e и скриншоты). В обычном запуске не ставится.
     */
    __nfgWindows?: {
      open(route: WindowRoute): void;
      offerBoosts(options: BoostOption[]): void;
      setPlayer(patch: Partial<PlayerState>): void;
      calls: [keyof WindowActions, unknown][];
      league?: LeagueResponse | null;
      friends?: FriendsResponse | null;
    };
  }
}

const features = import.meta.glob<{ register(): void }>("./*/window.tsx", { eager: true });

export function registerWindows(opts: { debug?: boolean } = {}): void {
  addMessages(windowMessages);
  for (const feature of Object.values(features)) feature.register();
  if (opts.debug) installDebug();
}

/** В журнал `calls` не идут: их подключает само приложение и в мок-режиме. */
const NOT_LOGGED: ReadonlySet<string> = new Set<keyof WindowActions>(["starterShown", "changeLocale"]);

function installDebug(): void {
  const calls: [keyof WindowActions, unknown][] = [];
  const log =
    <K extends keyof WindowActions>(k: K) =>
    (arg?: unknown) => {
      calls.push([k, arg]);
    };
  const logged = (Object.keys(NOOP_ACTIONS) as (keyof WindowActions)[])
    .filter((k) => !NOT_LOGGED.has(k))
    .map((k) => [k, log(k)]);
  setWindowActions({
    ...(Object.fromEntries(logged) as Partial<WindowActions>),
    league: async () => window.__nfgWindows?.league ?? null,
    friends: async () => window.__nfgWindows?.friends ?? null,
    buy: (product, place) => log("buy")({ product, place }),
    sendFeedback: async (input) => {
      log("sendFeedback")(input);
    },
  });
  window.__nfgWindows = {
    open: (route) => openWindow(route),
    offerBoosts: (options) => setBoostOffer(options),
    setPlayer(patch) {
      const p = appStore.get().player;
      if (p) setPlayer({ ...p, ...patch });
    },
    calls,
  };
}
